import invariant from 'tiny-invariant'

import { ReferralsImportItem } from '../../redux/slices/referralsImportBase'
import {
  CompanyEntity,
  ServiceAreaEntity,
  UserEntity,
} from '../../types/entities'
import { CreateHistoricalReferralRequest } from '../../types/requests/referrals'
import {
  DEFAULT_BULK_IMPORT_CIRCUMSTANCES,
  DEFAULT_BULK_IMPORT_REASON,
  REFERRAL_IMPORT_DIRECTION,
  REFERRAL_TYPE,
} from '../constants'

export function buildCreateHistoricalReferralRequest(
  importItem: Pick<
    ReferralsImportItem,
    | 'advisorEmail'
    | 'circumstances'
    | 'importDirection'
    | 'inviteAdvisor'
    | 'multipleInvites'
    | 'otherCompanyClient'
    | 'hasPausedClientEmails'
    | 'referralDate'
    | 'status'
    | 'userCompanyClient'
  > & {
    otherCompany: Pick<CompanyEntity, 'id'> | null
    otherCompanyUser: Pick<UserEntity, 'id'> | null
    serviceArea: Pick<ServiceAreaEntity, 'id'> | null
    userCompany: Pick<CompanyEntity, 'id'>
    userCompanyUser: Pick<UserEntity, 'id'>
  },
): CreateHistoricalReferralRequest {
  invariant(importItem?.userCompanyClient?.id, 'Expected client to be created')
  invariant(importItem.serviceArea, 'Expected service area to be set')

  const commonFields = {
    circumstances:
      importItem.circumstances ?? DEFAULT_BULK_IMPORT_CIRCUMSTANCES,
    reason: DEFAULT_BULK_IMPORT_REASON,
    type: REFERRAL_TYPE.indirect, // Direct will often be blocked by logic set out in API getAvailableReferralTypes()
    serviceAreaIds: [importItem.serviceArea?.id ?? 0],
    hasClientConsent: false,
    isImport: true,
    isHistoricalReferral: true as const,
    importStatusCode: importItem.status ?? undefined,
    referralDate: importItem.referralDate ?? undefined,
    skipInitialAdvisorInvite: !importItem.inviteAdvisor,
    hasPausedClientEmails: importItem.hasPausedClientEmails,
    asMultipleInvites: importItem.multipleInvites,
  }

  if (importItem.otherCompany) {
    // regular referrals
    if (importItem.importDirection === REFERRAL_IMPORT_DIRECTION.FROM) {
      // userCompany received referral from the other company
      return {
        ...commonFields,
        fromCompany: `/v1/companies/${importItem.otherCompany.id}`,
        fromCompanyUserOverwrite: importItem.otherCompanyUser
          ? `/v1/users/${importItem.otherCompanyUser.id}`
          : undefined,
        fromClient: importItem.otherCompanyClient
          ? importItem.otherCompanyClient['@id']
          : undefined,
        toCompany: `/v1/companies/${importItem.userCompany.id}`,
        toCompanyUsers: [`/v1/users/${importItem.userCompanyUser.id}`],
        toClient: importItem.userCompanyClient['@id'],
        referralInvites: [],
      }
    } else {
      // userCompany send referral to the other company
      return {
        ...commonFields,
        fromCompany: `/v1/companies/${importItem.userCompany.id}`,
        fromClient: importItem.userCompanyClient['@id'],
        toCompany: `/v1/companies/${importItem.otherCompany.id}`,
        toCompanyUsers: importItem.otherCompanyUser
          ? [`/v1/users/${importItem.otherCompanyUser.id}`]
          : [],
        referralInvites: [],
      }
    }
  } else {
    // invites
    if (importItem.importDirection === REFERRAL_IMPORT_DIRECTION.FROM) {
      // userCompany received referral from the new advisor
      return {
        ...commonFields,
        fromCompany: undefined,
        fromClient: undefined,
        toCompany: `/v1/companies/${importItem.userCompany.id}`,
        toCompanyUsers: [`/v1/users/${importItem.userCompanyUser.id}`],
        toClient: importItem.userCompanyClient['@id'],
        referralInvites: [
          {
            email: importItem.advisorEmail,
          },
        ],
      }
    } else {
      // userCompany send referral to the new advisor
      return {
        ...commonFields,
        fromCompany: `/v1/companies/${importItem.userCompany.id}`,
        fromClient: importItem.userCompanyClient['@id'],
        toCompany: undefined,
        toCompanyUsers: [],
        referralInvites: [
          {
            email: importItem.advisorEmail,
          },
        ],
      }
    }
  }
}
